import { maxBy } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { ApplyCode, LineItemType, Purchase, PurchaseRuleFilterErrorCode, SelectionState, ValidationLevel } from "types";
import { api } from "../../api/api";
import { isGift, isVoucherCoupon } from "../../models/Coupon";
import { PromptModal } from "../Common/PromptModal";
import { PriceStrategy } from "../PurchaseModification/PaymentBreakdown";

interface ApplyCouponCode {
    code?: ApplyCode;
    purchaseId: ID;
    amount?: number;
    voucher: string;
    isVerificationRequest?: boolean;
}

interface FieldError {
    message: string;
    reason: string;
}

const isItemIdInFilterError = (error: any) => {
    return error && error.code === PurchaseRuleFilterErrorCode.IN_FILTER_NOT_MET && error.field === "id";
};

const getUsageLimitError = (error: any): FieldError | null => {
    if (error?.field) {
        for (const [key, errorData] of Object.entries(error.field)) {
            if (key.includes("code") && (errorData as FieldError)?.reason === "code_usage_limit_exceeded") {
                return errorData as FieldError;
            }
        }
    }

    return null;
};

export const useApplySubmitCodeForm = ({
    selectedProducts,
    notifyGuests,
    showRetainPriceToggle,
    retainPrice,
    inputValue,
}: {
    selectedProducts?: SelectionState;
    notifyGuests: boolean;
    showRetainPriceToggle: boolean;
    retainPrice: boolean;
    inputValue?: string;
}) => {
    const [validationModalState, setValidationModalState] = useState<ValidationModalState>({
        isOpen: false,
        validationLevel: "",
        message: "",
    });

    const applyCouponCode = useMutation(
        async ({ code, amount, purchaseId, voucher, isVerificationRequest }: ApplyCouponCode) => {
            const couponCode = code ? code.code : inputValue;

            if (!couponCode) {
                return;
            }

            const payloadObject: any = {
                path: "/",
                op: "addCode",
                data: {
                    code: couponCode,
                    amount,
                },
            };
            let payload = [payloadObject];

            if (code && selectedProducts?.selected.length) {
                if (isVoucherCoupon(code?.coupon)) {
                    payload = [
                        {
                            ...payloadObject,
                            data: {
                                ...payloadObject.data,
                                appliedOn: selectedProducts.selected.map((selectedItem) => ({
                                    id: selectedItem,
                                })),
                            },
                        },
                    ];
                } else {
                    payload = selectedProducts.selected.map((id: ID) => {
                        return {
                            ...payloadObject,
                            path: `/items/${id}/lineItems`,
                        };
                    });
                }
            }

            if (code?.affiliateNotification) {
                // for Affiliates code we need to add additional fields
                payload = payload.map((item: any) => {
                    return {
                        ...item,
                        data: {
                            code: code?.code,
                            voucher,
                            affiliateDeposit: amount,
                        },
                    };
                });
            }

            if (isVerificationRequest) {
                const { data } = await api.patch<Purchase>(
                    `/purchases/${purchaseId}?validationLevel=${ValidationLevel.INFO}`,
                    {
                        ops: payload,
                        _emailOverrides: { sendEmailToTraveler: notifyGuests },
                        _priceStrategy: showRetainPriceToggle && retainPrice ? PriceStrategy.RETAIN : undefined,
                    },
                );
                return data;
            }

            try {
                const { data } = await api.patch<Purchase>(`/purchases/${purchaseId}`, {
                    ops: payload,
                    _emailOverrides: { sendEmailToTraveler: notifyGuests },
                    _priceStrategy: showRetainPriceToggle && retainPrice ? PriceStrategy.RETAIN : undefined,
                });
                return data;
            } catch (error: any) {
                const usageLimitError = getUsageLimitError(error?.response?.data);
                if (usageLimitError && !error?.response?.data?.errors) {
                    error.response.data.errors = [
                        {
                            code: usageLimitError.reason,
                            message: usageLimitError.message,
                            level: ValidationLevel.ERROR,
                            field: "appliedMembershipCouponCode.uses",
                        },
                    ];
                }

                const validationErrors = error?.response?.data?.errors;

                // Get the highest level
                const validationLevel: ValidationLevel =
                    maxBy(validationErrors, (error: any) => {
                        if (error.level === ValidationLevel.ERROR) {
                            return 3;
                        }

                        if (error.level === ValidationLevel.WARNING) {
                            return 2;
                        }

                        if (error.level === ValidationLevel.INFO) {
                            if (!isGift(code?.coupon)) {
                                error.level = ValidationLevel.ERROR;
                            }

                            return 1;
                        }

                        return 0;
                    })?.level ?? ValidationLevel.IGNORE;

                // Make an error array
                const errorFields = validationErrors
                    .filter((error: any) => !!errorFieldMap[error.field])
                    .map((error: any) => {
                        return errorFieldMap[error.field];
                    });

                const errorMessage =
                    validationLevel === ValidationLevel.ERROR
                        ? `This code does not meet ${errorFields.join(
                              ", ",
                          )} restrictions and cannot be applied on this purchase`
                        : `This code does not meet ${errorFields.join(
                              ", ",
                          )} restrictions. Using this code will result in voiding the promotional value on this gift. Do you wish to proceed?`;

                setValidationModalState({
                    isOpen: true,
                    validationLevel,
                    message: errorMessage,
                    errors: code?.membershipItem
                        ? validationErrors
                              ?.filter(
                                  (error: any) =>
                                      error.level === ValidationLevel.ERROR &&
                                      !!error.message &&
                                      !isItemIdInFilterError(error),
                              )
                              .map((error: any) => error.message)
                        : [],
                });
                throw new Error(error);
            }
        },
    );

    return { applyCouponCode, validationModalState, setValidationModalState };
};

const errorFieldMap: Record<string, string> = {
    arrivalDateTime: "arrival",
    "product.id": "product",
    currentTime: "purchase window",
    privacy: "privacy",
    quantity: "quantity",
};

export interface ValidationModalState {
    isOpen: boolean;
    validationLevel: string;
    message: string;
    errors?: string[];
}

export const ValidationModal = ({
    validationModalState,
    onClose,
    onSubmit,
}: {
    validationModalState: ValidationModalState;
    onClose: () => void;
    onSubmit: (e: any) => void;
}) => {
    return (
        <PromptModal
            title="Coupon Restrictions"
            isOpen={validationModalState.isOpen}
            readOnly={validationModalState.validationLevel === ValidationLevel.ERROR}
            onConfirm={onSubmit}
            onClose={onClose}
        >
            {validationModalState.message}

            {validationModalState.errors?.length ? (
                <ul className="ml-4 mt-2 list-disc">
                    {validationModalState.errors.map((errorMessage, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{errorMessage}</li>
                    ))}
                </ul>
            ) : null}
        </PromptModal>
    );
};

export const lineItemsToIgnoreWhenApplyingCode = [
    LineItemType.ADJUSTMENT,
    LineItemType.TAXED_ADJUSTMENT,
    LineItemType.DEMOGRAPHIC,
    LineItemType.DEMOGRAPHIC_SECONDARY,
    LineItemType.EQUIPMENT,
    LineItemType.WARE,
    LineItemType.ADDON,
    LineItemType.SUBTOTAL,
];
